import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAccountApi } from '../../../common/hooks/use-account-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { AccountNotice, AccountPage } from './components/AccountPage';

type Phase = { kind: 'checking' } | { kind: 'invalid' } | { kind: 'busy' } | { kind: 'done'; email: string };

/** Where a confirmation link lands (/verify-email/:token). Opening it is the confirmation. */
const VerifyEmailPage = () => {
    const { t } = useTranslation();
    const { token = '' } = useParams();
    const navigate = useNavigate();
    const accountApi = useAccountApi();
    const [phase, setPhase] = useState<Phase>({ kind: 'checking' });
    // The link works once: in development React runs effects twice, and a
    // second call would report the link as used.
    const asked = useRef<string | null>(null);

    useEffect(() => {
        if (asked.current === token) return;
        asked.current = token;
        accountApi
            .verifyEmail(token)
            .then(({ email }) => setPhase({ kind: 'done', email }))
            .catch(async (e) => {
                const { status } = await readApiError(e);
                setPhase(status === 429 ? { kind: 'busy' } : { kind: 'invalid' });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return (
        <AccountPage>
            {phase.kind === 'checking' && (
                <>
                    <p className="mb-4 text-center text-sm text-muted-foreground" role="status">{t('ACCOUNT.VERIFY.CHECKING')}</p>
                    <Skeleton className="h-24 rounded-xl" />
                </>
            )}
            {phase.kind === 'done' && (
                <AccountNotice tone="success" title={t('ACCOUNT.VERIFY.DONE_TITLE')} testId="verify-done">
                    {t('ACCOUNT.VERIFY.DONE', { email: phase.email })}
                </AccountNotice>
            )}
            {phase.kind === 'busy' && (
                <AccountNotice tone="error" testId="verify-busy">{t('ACCOUNT.COMMON.TOO_MANY')}</AccountNotice>
            )}
            {phase.kind === 'invalid' && (
                <AccountNotice tone="error" title={t('ACCOUNT.VERIFY.INVALID_TITLE')} testId="verify-invalid">
                    {t('ACCOUNT.VERIFY.INVALID')}
                </AccountNotice>
            )}
            {phase.kind !== 'checking' && (
                <Button className="h-11 w-full" onClick={() => navigate('/login')}>
                    {t('ACCOUNT.INVITE.GO_TO_LOGIN')}
                </Button>
            )}
        </AccountPage>
    );
};

export default VerifyEmailPage;
